/**
 * The fixture board the table view runs on until the tRPC source lands (round 3).
 *
 * `sandbox-board.json` was generated from `discovery/api-corpus/` — the **sandbox** workspace
 * only (17435479, boards 18430299563 / 18430366573 / 18430366977), never production. Every
 * `values` entry is monday's decoded `ColumnValue.value` for that type and every `settings`
 * entry is a decoded `settings_str`, so the shared registry parses them without a single
 * adapter: `{"index":0}` for status, `{"ids":[1,2]}` for dropdown, `"SKU-1000"` for text,
 * `{"personsAndTeams":[…]}` for people, `{"date":"…","time":"…"}` for date `[cvs§2]`.
 *
 * It is deliberately bigger than the recorded sandbox (204 items over 3 groups, 13 columns):
 * L2 and L5 need more rows than fit on one screen, and the group summaries need a spread.
 */
import type { BoardId, ColumnAggregation, ColumnId, GroupId, ItemId, UserId, ViewId } from '@elizmonday/shared';
import raw from './sandbox-board.json';
import { normaliseColumnSettings } from '../columns/registry';
import { InMemoryBoardDataSource, type MemoryBoardSeed } from './memory';
import type { BoardColumn, ItemRow, Person } from './types';
import type { TableViewSettings } from '../table/settings';

interface RawColumn {
  id: string;
  type: string;
  title: string;
  description: string | null;
  settings: unknown;
  width: number | null;
  position: number;
  revision: string;
  aggregation: string;
}

interface RawItem {
  id: string;
  name: string;
  groupId: string;
  position: number;
  updatesCount: number;
  subitemIds: string[];
  values: Record<string, unknown>;
}

interface RawBoard {
  board: { id: string; name: string; itemTerminology: string; itemTerminologyPlural: string };
  me: string;
  people: { id: string; name: string; email: string; color: string }[];
  views: { id: string; name: string; type: string; isDefault: boolean; ownerId: string | null }[];
  columns: RawColumn[];
  groups: { id: string; title: string; color: string; position: number }[];
  items: RawItem[];
}

const fixture = raw as unknown as RawBoard;

export const FIXTURE_BOARD_ID = fixture.board.id as BoardId;
export const FIXTURE_BOARD_NAME = fixture.board.name;
export const FIXTURE_VIEW_ID = fixture.views[0]!.id as ViewId;

function toColumn(column: RawColumn): BoardColumn {
  return {
    id: column.id as ColumnId,
    type: column.type,
    title: column.title,
    description: column.description,
    // One shape for every consumer, whichever of monday's two serialisations the capture
    // holds: this fixture stores `mirror.displayed_linked_columns` in its **array** form and
    // that took the board route down in round 5t (`../columns/registry.ts`, `T-5t`).
    settings: normaliseColumnSettings(column.type, column.settings),
    width: column.width,
    position: column.position,
    revision: column.revision,
    aggregation: column.aggregation as ColumnAggregation,
  };
}

function toItem(item: RawItem): ItemRow {
  return {
    id: item.id as ItemId,
    name: item.name,
    groupId: item.groupId as GroupId,
    position: item.position,
    updatesCount: item.updatesCount,
    subitemIds: item.subitemIds as ItemId[],
    values: item.values,
  };
}

function toPerson(person: RawBoard['people'][number]): Person {
  return {
    id: person.id as UserId,
    name: person.name,
    email: person.email,
    photoUrl: null,
    color: person.color,
  };
}

/**
 * M3.S1: the fixture's 24 parents already carry `subitemIds` (48 rows), but no subitem rows —
 * the nested table needs both. The subitems board is built here to the exact shape monday
 * seeds `[sub§2]`: `name` "Name", `person` "Owner", `status` "Status" (its own settings, with
 * `hide_footer`), `date0` "Date".
 */
function subitemColumns(): BoardColumn[] {
  /**
   * The three label colours are lifted from the recorded board's own `status` column rather
   * than written out here: the lint rule bans raw hex outside `packages/ui/src/tokens`, and
   * the captured settings are the truth anyway `[cvs§2 status]`.
   */
  const recorded = fixture.columns.find((column) => column.type === 'status');
  const labelsColors = (recorded?.settings as { labels_colors?: Record<string, unknown> } | undefined)
    ?.labels_colors;
  const pick = (index: string) => (labelsColors ? labelsColors[index] : undefined);

  return [
    { id: 'name' as ColumnId, type: 'name', title: 'Subitem', description: null, settings: {}, width: 300, position: 0, revision: '1', aggregation: 'none' },
    { id: 'person' as ColumnId, type: 'people', title: 'Owner', description: null, settings: {}, width: 98, position: 1, revision: '1', aggregation: 'none' },
    {
      id: 'status' as ColumnId,
      type: 'status',
      title: 'Status',
      description: null,
      settings: {
        done_colors: [1],
        // monday seeds the subitems board's status column with the footer hidden `[sub:38]`.
        hide_footer: true,
        labels: { 0: 'Working on it', 1: 'Done', 2: 'Stuck' },
        labels_colors: { 0: pick('0'), 1: pick('1'), 2: pick('2') },
      },
      width: 136,
      position: 2,
      revision: '1',
      aggregation: 'none',
    },
    { id: 'date0' as ColumnId, type: 'date', title: 'Date', description: null, settings: {}, width: 140, position: 3, revision: '1', aggregation: 'none' },
  ];
}

function fixtureSubitems(items: readonly ItemRow[]): Record<string, ItemRow[]> {
  const out: Record<string, ItemRow[]> = {};
  for (const parent of items) {
    if (parent.subitemIds.length === 0) continue;
    out[String(parent.id)] = parent.subitemIds.map((subitemId, index) => ({
      id: subitemId,
      name: `${parent.name} · subitem ${String(index + 1)}`,
      groupId: 'topics' as GroupId,
      position: index,
      updatesCount: 0,
      subitemIds: [],
      values: {
        name: `${parent.name} · subitem ${String(index + 1)}`,
        status: { index: index % 3 },
      },
    }));
  }
  return out;
}

export function fixtureSeed(overrides: Partial<MemoryBoardSeed> = {}): MemoryBoardSeed {
  const items = fixture.items.map(toItem);
  return {
    board: {
      id: fixture.board.id as BoardId,
      name: fixture.board.name,
      itemTerminology: fixture.board.itemTerminology,
      itemTerminologyPlural: fixture.board.itemTerminologyPlural,
    },
    me: fixture.me as UserId,
    people: fixture.people.map(toPerson),
    views: fixture.views.map((view) => ({
      id: view.id as ViewId,
      name: view.name,
      type: view.type,
      isDefault: view.isDefault,
      ownerId: view.ownerId as UserId | null,
    })),
    columns: fixture.columns.map(toColumn),
    groups: fixture.groups.map((group) => ({ ...group, id: group.id as GroupId })),
    items,
    subitemColumns: subitemColumns(),
    subitems: fixtureSubitems(items),
    /*
     * **P-122**, and **ASSUMED**: the sandbox capture holds no populated `tags` cell at all
     * (`TagsValue.tags` is `[]` on every sampled item), so the three ids the fixture board's
     * cells carry have no captured names. These are fixture names for the harness — the
     * product reads the account vocabulary from the api, never from here.
     */
    tags: [
      // No colour: monday's `tags { color }` was never captured for these either, and a chip
      // without one falls back to the picker's default rather than to an invented palette.
      { id: 101, name: 'Design' },
      { id: 102, name: 'Research' },
      { id: 103, name: 'Urgent' },
    ],
    // The capture ran in a UTC-rendering session; pinning it keeps the screenshots stable.
    timezone: 'UTC',
    ...overrides,
  };
}

export function createFixtureDataSource(
  overrides: Partial<MemoryBoardSeed> = {},
): InMemoryBoardDataSource {
  return new InMemoryBoardDataSource(fixtureSeed(overrides));
}

/** A tiny seed for unit tests: two groups, four items, the columns the test names. */
export function smallSeed(
  columnIds: readonly string[] = ['name', 'status', 'text', 'numbers'],
  viewSettings?: Record<string, Partial<TableViewSettings>>,
): MemoryBoardSeed {
  const seed = fixtureSeed();
  const wanted = new Set(columnIds);
  const groups = seed.groups.slice(0, 2);
  const keep = new Set(groups.map((group) => String(group.id)));
  const items = seed.items.filter((item) => keep.has(String(item.groupId))).slice(0, 4);
  return {
    ...seed,
    columns: seed.columns.filter((column) => wanted.has(column.id)),
    groups,
    items,
    viewSettings,
  };
}
